import { useState, useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Button } from "@/shared/components/ui/button";
import { Textarea } from "@/shared/components/ui/textarea";
import { Spinner } from "@/shared/components/ui/spinner";
import { Check, X, Search } from "lucide-react";
import { RentalType } from "@/shared/types/enums";
import type { TagResponseDTO } from "@/features/tags/types";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import type { StockResponseDTO } from "@/features/stocks/types/stock.types";
import { step2Schema, type Step2Data } from "../../schemas";

interface WizardStep2Props {
  defaultValues?: Partial<Step2Data>;
  tags: TagResponseDTO[];
  gownProducts: ProductResponseDTO[];
  searchStocks: (
    term: string,
    signal?: AbortSignal,
  ) => Promise<StockResponseDTO[]>;
  onNext: (data: Step2Data) => void;
  onBack: () => void;
}

export const WizardStep2 = ({
  defaultValues,
  tags,
  gownProducts,
  searchStocks,
  onNext,
  onBack,
}: WizardStep2Props) => {
  const form = useForm<Step2Data>({
    resolver: zodResolver(step2Schema),
    defaultValues: {
      rentalType: defaultValues?.rentalType ?? RentalType.SUIT,
      stockIds: defaultValues?.stockIds ?? [],
      gownItems: defaultValues?.gownItems ?? [],
      hasShoes: defaultValues?.hasShoes ?? false,
      shoesPairQuantity: defaultValues?.shoesPairQuantity ?? 0,
      description: defaultValues?.description ?? "",
      tagIds: defaultValues?.tagIds ?? [],
    },
  });

  const rentalType = form.watch("rentalType");
  const hasShoes = form.watch("hasShoes");
  const stockIds = form.watch("stockIds");
  const gownItems = form.watch("gownItems");
  const tagIds = form.watch("tagIds");

  const [stockTerm, setStockTerm] = useState("");
  const [stockResults, setStockResults] = useState<StockResponseDTO[]>([]);
  const [stockSearching, setStockSearching] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [stockDetails, setStockDetails] = useState<
    Map<number, StockResponseDTO>
  >(new Map());
  const stockContainerRef = useRef<HTMLDivElement>(null);

  const [gownProductId, setGownProductId] = useState<number>(0);
  const [gownQty, setGownQty] = useState(1);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        stockContainerRef.current &&
        !stockContainerRef.current.contains(e.target as Node)
      ) {
        setStockOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!stockTerm.trim()) {
      setStockResults([]);
      setStockOpen(false);
      return;
    }
    const t = setTimeout(async () => {
      setStockSearching(true);
      try {
        const results = await searchStocks(stockTerm);
        setStockResults(results);
        setStockOpen(true);
      } finally {
        setStockSearching(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [stockTerm, searchStocks]);

  const handleAddStock = useCallback(
    (stock: StockResponseDTO) => {
      const current = form.getValues("stockIds");
      if (current.includes(stock.id)) return;
      setStockDetails((prev) => new Map(prev).set(stock.id, stock));
      form.setValue("stockIds", [...current, stock.id]);
      setStockTerm("");
      setStockResults([]);
      setStockOpen(false);
    },
    [form],
  );

  const handleRemoveStock = useCallback(
    (id: number) => {
      form.setValue(
        "stockIds",
        form.getValues("stockIds").filter((s) => s !== id),
      );
      setStockDetails((prev) => {
        const m = new Map(prev);
        m.delete(id);
        return m;
      });
    },
    [form],
  );

  const handleAddGown = useCallback(() => {
    if (!gownProductId) return;
    const exists = gownItems.find((g) => g.productId === gownProductId);
    if (exists) {
      form.setValue(
        "gownItems",
        gownItems.map((g) =>
          g.productId === gownProductId
            ? { ...g, quantity: g.quantity + gownQty }
            : g,
        ),
      );
    } else {
      form.setValue("gownItems", [
        ...gownItems,
        { productId: gownProductId, quantity: gownQty },
      ]);
    }
    setGownQty(1);
  }, [gownProductId, gownQty, gownItems, form]);

  const toggleTag = useCallback(
    (id: number) => {
      form.setValue(
        "tagIds",
        tagIds.includes(id) ? tagIds.filter((t) => t !== id) : [...tagIds, id],
      );
    },
    [tagIds, form],
  );

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onNext)} className="space-y-6">
        <FormField
          control={form.control}
          name="rentalType"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Tipo de locação *</FormLabel>
              <FormControl>
                <div className="flex gap-3">
                  {[RentalType.SUIT, RentalType.GOWN].map((t) => (
                    <button
                      key={t}
                      type="button"
                      onClick={() => {
                        field.onChange(t);
                        form.setValue("stockIds", []);
                        form.setValue("gownItems", []);
                      }}
                      className={`flex-1 h-11 rounded-md border text-sm font-medium transition-colors ${field.value === t ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-accent border-input"}`}
                    >
                      {t === RentalType.SUIT ? "Terno" : "Beca"}
                    </button>
                  ))}
                </div>
              </FormControl>
            </FormItem>
          )}
        />

        {rentalType === RentalType.SUIT ? (
          <div className="space-y-3">
            <FormLabel>Peças do estoque</FormLabel>
            <div ref={stockContainerRef} className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Buscar por ID ou código de barras..."
                value={stockTerm}
                onChange={(e) => {
                  setStockTerm(e.target.value);
                  setStockOpen(true);
                }}
                onFocus={() => stockResults.length > 0 && setStockOpen(true)}
                className="pl-9 h-11"
              />
              {stockOpen &&
                (stockSearching || stockResults.length > 0 || stockTerm) && (
                  <div className="absolute z-50 mt-1 w-full rounded-md border bg-background shadow-md">
                    {stockSearching ? (
                      <div className="flex items-center gap-2 px-3 py-2.5 text-sm text-muted-foreground">
                        <Spinner className="w-4 h-4" /> Buscando...
                      </div>
                    ) : stockResults.length === 0 ? (
                      <p className="px-3 py-2.5 text-sm text-muted-foreground">
                        Nenhum estoque encontrado
                      </p>
                    ) : (
                      <ul className="max-h-48 overflow-y-auto py-1">
                        {stockResults.map((s) => {
                          const alreadyAdded = stockIds.includes(s.id);
                          return (
                            <li key={s.id}>
                              <button
                                type="button"
                                disabled={alreadyAdded}
                                onClick={() => handleAddStock(s)}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-between"
                              >
                                <span>
                                  <span className="font-medium">
                                    {s.barcode}
                                  </span>
                                  <span className="ml-2 text-muted-foreground">
                                    {s.productName} — {s.size}
                                  </span>
                                </span>
                                {alreadyAdded && (
                                  <Check className="w-4 h-4 text-primary shrink-0" />
                                )}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                )}
            </div>
            {stockIds.length > 0 && (
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {stockIds.map((id) => {
                  const s = stockDetails.get(id);
                  return (
                    <li
                      key={id}
                      className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/50 text-sm"
                    >
                      <span>
                        {s
                          ? `${s.barcode} — ${s.productName} (${s.size})`
                          : `ID ${id}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleRemoveStock(id)}
                        aria-label="Remover peça"
                      >
                        <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            <div className="flex items-center gap-3 pt-1">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasShoes}
                  onChange={(e) => {
                    form.setValue("hasShoes", e.target.checked);
                    if (!e.target.checked)
                      form.setValue("shoesPairQuantity", 0);
                  }}
                  className="rounded"
                />
                Incluir sapato
              </label>
              {hasShoes && (
                <FormField
                  control={form.control}
                  name="shoesPairQuantity"
                  render={({ field }) => (
                    <FormItem className="flex items-center gap-2 m-0">
                      <FormLabel className="text-sm m-0">Pares</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          className="w-24 h-9"
                          {...field}
                          onChange={(e) =>
                            field.onChange(Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <FormLabel>Itens de beca</FormLabel>
            <div className="flex gap-2">
              <select
                value={gownProductId}
                onChange={(e) => setGownProductId(Number(e.target.value))}
                className="flex-1 h-11 rounded-md border border-input bg-background px-3 text-sm"
              >
                <option value={0}>Selecione o produto</option>
                {gownProducts.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name || `${p.brand} ${p.color}`}
                  </option>
                ))}
              </select>
              <Input
                type="number"
                min={1}
                value={gownQty}
                onChange={(e) => setGownQty(Number(e.target.value))}
                className="w-24 h-11"
                placeholder="Qtd"
              />
              <Button
                type="button"
                onClick={handleAddGown}
                disabled={!gownProductId}
                className="h-11 px-4"
              >
                Adicionar
              </Button>
            </div>
            {gownItems.length > 0 && (
              <ul className="space-y-1.5 max-h-40 overflow-y-auto">
                {gownItems.map((item) => {
                  const p = gownProducts.find((g) => g.id === item.productId);
                  return (
                    <li
                      key={item.productId}
                      className="flex items-center justify-between px-3 py-2 rounded-md bg-muted/50 text-sm"
                    >
                      <span>
                        {p
                          ? p.name || `${p.brand} ${p.color}`
                          : `Produto ${item.productId}`}{" "}
                        — {item.quantity} un.
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          form.setValue(
                            "gownItems",
                            gownItems.filter(
                              (g) => g.productId !== item.productId,
                            ),
                          )
                        }
                        aria-label="Remover item"
                      >
                        <X className="w-4 h-4 text-muted-foreground hover:text-destructive" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {tags.length > 0 && (
          <div className="space-y-2">
            <FormLabel>Tags</FormLabel>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => {
                const selected = tagIds.includes(tag.id);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium border transition-all"
                    style={{
                      borderColor: tag.color,
                      backgroundColor: selected ? tag.color : "transparent",
                      color: selected ? "#fff" : tag.color,
                    }}
                  >
                    {selected && <Check className="w-3 h-3 shrink-0" />}
                    {tag.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Descrição</FormLabel>
              <FormControl>
                <Textarea
                  placeholder="Observações sobre o aluguel..."
                  maxLength={200}
                  rows={3}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex justify-between pt-2">
          <Button type="button" variant="outline" onClick={onBack}>
            Voltar
          </Button>
          <Button type="submit">Próximo</Button>
        </div>
      </form>
    </Form>
  );
};
